import { supabase } from '@/integrations/supabase/client';
import { comprehensiveNotificationService } from '@/services/comprehensiveNotificationService';

interface ShopSubscriber {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  username: string;
  email?: string;
  subscribed_at: string;
  is_notified: boolean;
}

export const shopNotificationService = {
  async subscribeToLaunch(
    userId: string,
    userType: 'supabase' | 'internal',
    username: string,
    email?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('shop_launch_subscribers')
        .insert({
          user_id: userId,
          user_type: userType,
          username,
          email
        });

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'You are already subscribed' };
        }
        console.error('Error subscribing:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in subscribeToLaunch:', error);
      return { success: false, error: 'Failed to subscribe' };
    }
  },

  async isSubscribed(userId: string, userType: 'supabase' | 'internal'): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('shop_launch_subscribers')
        .select('id')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .maybeSingle();

      if (error) {
        console.error('Error checking subscription:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error in isSubscribed:', error);
      return false;
    }
  },

  async unsubscribe(userId: string, userType: 'supabase' | 'internal'): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('shop_launch_subscribers')
        .delete()
        .eq('user_id', userId)
        .eq('user_type', userType);

      return !error;
    } catch (error) {
      console.error('Error in unsubscribe:', error);
      return false;
    }
  },

  async getSubscriberCount(): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('shop_launch_subscribers')
        .select('*', { count: 'exact', head: true })
        .eq('is_notified', false);

      if (error) {
        console.error('Error getting subscriber count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getSubscriberCount:', error);
      return 0;
    }
  },

  async getAllSubscribers(): Promise<ShopSubscriber[]> {
    try {
      const { data, error } = await supabase
        .from('shop_launch_subscribers')
        .select('*')
        .eq('is_notified', false);

      if (error) {
        console.error('Error getting subscribers:', error);
        return [];
      }

      return (data || []) as ShopSubscriber[];
    } catch (error) {
      console.error('Error in getAllSubscribers:', error);
      return [];
    }
  },

  async notifyAllSubscribers(adminId: string): Promise<{ success: boolean; notifiedCount: number }> {
    try {
      const subscribers = await this.getAllSubscribers();
      
      if (subscribers.length === 0) {
        return { success: true, notifiedCount: 0 };
      }

      let notifiedCount = 0;

      for (const sub of subscribers) {
        const notification = await comprehensiveNotificationService.createNotification({
          user_id: sub.user_id,
          user_type: sub.user_type as 'supabase' | 'internal',
          notification_type: 'shop_launch',
          category: 'general',
          title: '🛒 The Shop is NOW LIVE!',
          message: 'Great news! The Players Lockeroom Shop is now open. Check out our premium sports gear, equipment, and exclusive merchandise!',
          priority: 'high',
          action_url: '/shop',
          metadata: { 
            source: 'shop_launch', 
            notified_by: adminId,
            subscriber_username: sub.username
          }
        });

        if (notification) {
          notifiedCount++;
        }
      }

      // Mark all subscribers as notified
      const { error } = await supabase
        .from('shop_launch_subscribers')
        .update({ 
          is_notified: true, 
          notified_at: new Date().toISOString() 
        })
        .eq('is_notified', false);

      if (error) {
        console.error('Error marking subscribers as notified:', error);
      }

      return { success: true, notifiedCount };
    } catch (error) {
      console.error('Error in notifyAllSubscribers:', error);
      return { success: false, notifiedCount: 0 };
    }
  }
};
